import React from 'react';
import { useTranslation } from '../../i18n';

interface WaitingSpinnerProps {
  message?: string;
  isLoading?: boolean;
  size?: 'small' | 'medium' | 'large';
  /** Override spinner/text color. Defaults to pack primary (loading) or gold (waiting). */
  color?: string;
  /** Tighter vertical spacing (bonus overlays / dense mobile layouts). */
  compact?: boolean;
}

const WaitingSpinner: React.FC<WaitingSpinnerProps> = ({
  message,
  isLoading = false,
  size = 'small',
  color,
  compact = false,
}) => {
  const { t } = useTranslation();

  const spinnerSizes = {
    small: { width: '32px', height: '32px', border: '3px' },
    medium: { width: '40px', height: '40px', border: '4px' },
    large: { width: '50px', height: '50px', border: '5px' },
  };

  const { width, height, border } = spinnerSizes[size];
  const defaultMessage = isLoading
    ? t('gameplay.loading.nextQuestion')
    : t('gameplay.loading.waitingForOthers');

  const accent = color ?? (isLoading ? 'var(--color-primary)' : '#ca8a04');
  const track = color
    ? `${accent}33`
    : isLoading
      ? 'rgba(var(--color-primary-rgb), 0.2)'
      : 'rgba(202, 138, 4, 0.25)';

  return (
    <div
      className={`waiting-spinner${compact ? ' waiting-spinner--compact' : ''}`}
      style={{
        textAlign: 'center',
        paddingTop: compact ? '0.35rem' : '1rem',
        marginTop: compact ? '0.5rem' : '2rem',
        marginBottom: compact ? '0.35rem' : '1rem',
      }}
    >
      <div
        className={`waiting-spinner__ring${isLoading ? '' : ' waiting-spinner__ring--waiting'}`}
        style={{
          display: 'inline-block',
          width,
          height,
          border: `${border} solid ${track}`,
          borderTop: `${border} solid ${accent}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '0.5rem',
        }}
      />
      <p
        className={`waiting-spinner__text${isLoading ? ' waiting-spinner__text--loading' : ' waiting-spinner__text--waiting'}`}
        style={{
          color: accent,
          fontSize: compact ? '0.95rem' : '1rem',
          fontWeight: 'bold',
          margin: 0,
        }}
      >
        {message || defaultMessage}
      </p>
    </div>
  );
};

export default WaitingSpinner;
